import { END_OF_DAY_LOAD_TARGET_SUSPENSION_CODE } from "../utils/endOfDayLoadTargetSuspension.js";
import { buildCompartmentSharingRetryBody, COMPARTMENT_SHARING_CHALLENGE_CODE } from "../utils/compartmentSharingChallenge.js";
import { fetchWithSupabaseAuth } from "./supabaseClient.js";
import { fetchWithOrderDuplicateConfirmation, OrderDuplicateConfirmationCancelled } from "../utils/orderDuplicateConfirmation.js";
import { loadPageData, invalidatePageDataCacheEntries } from "../hooks/usePageData.js";
import { RAILWAY_BUSINESS_COPY_PROGRESS_STEPS } from "../utils/toastProgress.js";

function resolveErrorMessage(payload, fallbackMessage) {
  if (payload && typeof payload.detail === "string" && payload.detail.trim()) {
    return payload.detail.trim();
  }

  // Saisie refusée (422) : seul un message rédigé par le SaaS est montré, jamais le texte anglais automatique.
  const writtenIssue = Array.isArray(payload?.detail)
    ? payload.detail.map((issue) => String(issue?.msg || "")).find((msg) => msg.startsWith("Value error, "))
    : "";
  if (writtenIssue) {
    return writtenIssue.slice("Value error, ".length).trim();
  }

  if (payload && payload.detail && typeof payload.detail === "object") {
    const detailMessage = typeof payload.detail.message === "string" ? payload.detail.message.trim() : "";
    const detailError = typeof payload.detail.error === "string" ? payload.detail.error.trim() : "";
    const detailText = typeof payload.detail.detail === "string" ? payload.detail.detail.trim() : "";
    const issueMessages = Array.isArray(payload.detail.issues)
      ? payload.detail.issues
        .map((issue) => (typeof issue?.message === "string" ? issue.message.trim() : ""))
        .filter(Boolean)
      : [];
    const nestedMessages = [...new Set([detailMessage, detailError, detailText, ...issueMessages].filter(Boolean))];
    if (nestedMessages.length > 0) {
      const stage = RAILWAY_BUSINESS_COPY_PROGRESS_STEPS.find((step) => step.key === payload.detail.stage);
      return [...nestedMessages, ...(stage ? [`Étape : ${stage.label}.`] : [])].join(" ");
    }
  }

  if (payload && typeof payload.error === "string" && payload.error.trim()) {
    return payload.error.trim();
  }

  if (payload && typeof payload.message === "string" && payload.message.trim()) {
    return payload.message.trim();
  }

  return fallbackMessage;
}

async function parseJsonResponse(response, fallbackMessage) {
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(resolveErrorMessage(payload, fallbackMessage));
    error.status = response.status;
    throw error;
  }

  return payload;
}

async function requestJson(url, init, fallbackMessage, { onConfirmationChange } = {}) {
  let response = await fetchWithOrderDuplicateConfirmation(fetchWithSupabaseAuth, url, init, { onConfirmationChange });
  if (response.status === 428 && typeof window !== "undefined") {
    const accepted = await new Promise((resolve) => {
      const detail = { resolve, handled: false };
      window.dispatchEvent(new CustomEvent("grain-confirm-employee-identity", { detail }));
      if (!detail.handled) resolve(false);
    });
    if (accepted) response = await fetchWithSupabaseAuth(url, { ...init, headers: { ...init?.headers, "X-Grain-Confirm-Shared-Identity": "true" } });
  }
  // Conversion en dompeur : confirmer le détachement des attelages datés, puis un seul nouvel envoi.
  if (response.status === 409 && typeof window !== "undefined" && typeof init?.body === "string") {
    const challenge = (await response.clone().json().catch(() => null))?.detail;
    if (challenge?.code === "dompeur_conversion_confirmation_required") {
      const accepted = await new Promise((resolve) => {
        const detail = { challenge, resolve, handled: false };
        window.dispatchEvent(new CustomEvent("grain-confirm-dompeur-conversion", { detail }));
        if (!detail.handled) resolve(false);
      });
      if (accepted) {
        response = await fetchWithSupabaseAuth(url, {
          ...init, body: JSON.stringify({ ...JSON.parse(init.body), dompeur_conversion_versions: challenge.versions }),
        });
      }
    }
  }
  // Chargé PM : compléter un compartiment entamé du même produit, puis la suspension cible ; chaque relance garde la réponse.
  let requestBody = typeof init?.body === "string" ? init.body : null;
  if (response.status === 409 && typeof window !== "undefined" && requestBody) {
    const challenge = await response.clone().json().catch(() => null);
    if (challenge?.code === COMPARTMENT_SHARING_CHALLENGE_CODE) {
      const accepted = await new Promise((resolve) => {
        const detail = { challenge, resolve, handled: false };
        window.dispatchEvent(new CustomEvent("grain-confirm-compartment-sharing", { detail }));
        if (!detail.handled) resolve(false);
      });
      if (!accepted) throw new OrderDuplicateConfirmationCancelled();
      requestBody = JSON.stringify(buildCompartmentSharingRetryBody(JSON.parse(requestBody), challenge.error_details, "share"));
      response = await fetchWithSupabaseAuth(url, { ...init, body: requestBody });
    }
  }
  // Chargé PM : le répartiteur choisit avant qu'une commande quitte le camion cible.
  if (response.status === 409 && typeof window !== "undefined" && requestBody) {
    const challenge = await response.clone().json().catch(() => null);
    if (challenge?.code === END_OF_DAY_LOAD_TARGET_SUSPENSION_CODE) {
      const decision = await new Promise((resolve) => {
        const detail = { challenge, resolve, handled: false };
        window.dispatchEvent(new CustomEvent("grain-confirm-pm-target-suspension", { detail }));
        if (!detail.handled) resolve(null);
      });
      // Annuler n'est pas une erreur : même traitement silencieux qu'une confirmation de doublon refusée.
      if (!decision) throw new OrderDuplicateConfirmationCancelled();
      response = await fetchWithSupabaseAuth(url, {
        ...init, body: JSON.stringify({ ...JSON.parse(requestBody), target_suspension_decision: decision }),
      });
    }
  }
  return parseJsonResponse(response, fallbackMessage);
}

async function downloadDataManagerFile(url, fileName, fallbackMessage) {
  const response = await fetchWithSupabaseAuth(url);
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(resolveErrorMessage(payload, fallbackMessage));
  }

  const blob = await response.blob();
  downloadBlob(blob, fileName);
}

function downloadBlob(blob, fileName) {
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = window.document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.rel = "noopener";
  window.document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => {
    window.URL.revokeObjectURL(objectUrl);
  }, 120000);
}

export function createCatalogProduct(payload) {
  return requestJson("/api/data-manager/product-catalog", {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
  }, "Impossible de créer le produit.");
}

export function updateCatalogProduct(productId, payload) {
  return requestJson(`/api/data-manager/product-catalog/${encodeURIComponent(productId)}`, {
    method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
  }, "Impossible d'enregistrer le produit.");
}

export function previewCatalogProductRename(productId) {
  return requestJson(`/api/data-manager/product-catalog/${encodeURIComponent(productId)}/rename-preview`, {
    method: "GET",
  }, "Impossible de compter ce que le renommage touche.");
}

export function deleteCatalogProduct(productId, payload) {
  return requestJson(`/api/data-manager/product-catalog/${encodeURIComponent(productId)}`, {
    method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
  }, "Impossible de supprimer le produit.");
}

export async function fetchDataManagerState({
  sections = [],
  force = false,
  orderDeliveryDate = "",
  orderDeliveryDateFrom = "",
  orderDeliveryDateTo = "",
  orderScope = "",
  orderBillingApprovalStatus = "",
  historyScope = "",
  historyMode = "",
} = {}) {
  const searchParams = new URLSearchParams();
  if (Array.isArray(sections)) {
    sections.forEach((section) => {
      if (typeof section === "string" && section.trim()) {
        searchParams.append("sections", section.trim());
      }
    });
  }
  if (typeof orderScope === "string" && orderScope.trim()) {
    searchParams.append("order_scope", orderScope.trim());
  }
  if (typeof orderDeliveryDate === "string" && orderDeliveryDate.trim()) {
    searchParams.append("order_delivery_date", orderDeliveryDate.trim());
  }
  if (typeof orderDeliveryDateFrom === "string" && orderDeliveryDateFrom.trim()) {
    searchParams.append("order_delivery_date_from", orderDeliveryDateFrom.trim());
  }
  if (typeof orderDeliveryDateTo === "string" && orderDeliveryDateTo.trim()) {
    searchParams.append("order_delivery_date_to", orderDeliveryDateTo.trim());
  }
  if (typeof orderBillingApprovalStatus === "string" && orderBillingApprovalStatus.trim()) {
    searchParams.append("order_billing_approval_status", orderBillingApprovalStatus.trim());
  }
  if (typeof historyScope === "string" && historyScope.trim()) {
    searchParams.append("history_scope", historyScope.trim());
  }
  if (typeof historyMode === "string" && historyMode.trim()) {
    searchParams.append("history_mode", historyMode.trim());
  }

  const queryString = searchParams.toString();
  const requestUrl = queryString ? `/api/data-manager/state?${queryString}` : "/api/data-manager/state";
  return loadPageData(requestUrl, { force });
}

export async function fetchDispatchDayContext() {
  return requestJson(
    "/api/dispatch-days",
    undefined,
    "Impossible de charger les journées de dispatch.",
  );
}

export async function fetchDataManagerHistoryEntry(auditId) {
  return requestJson(
    `/api/data-manager/history/${encodeURIComponent(auditId)}`,
    undefined,
    "Impossible de charger le détail de traçabilité."
  );
}

export async function createTruck(payload) {
  return requestJson("/api/data-manager/trucks", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de créer le camion.");
}

export async function createClientCorrespondence(payload) {
  return requestJson("/api/data-manager/client-correspondences", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de créer le client.");
}

export async function createEmployee(payload) {
  return requestJson("/api/data-manager/employees", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de créer le chauffeur.");
}

export async function createProduct(payload) {
  return requestJson("/api/data-manager/products", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de créer le code produit.");
}

export async function updateTruck(truckId, payload) {
  return requestJson(`/api/data-manager/trucks/${encodeURIComponent(truckId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible d'enregistrer le camion.");
}

export function previewClientCorrespondenceRename(correspondenceId) {
  return requestJson(`/api/data-manager/client-correspondences/${encodeURIComponent(correspondenceId)}/rename-preview`, {
    method: "GET",
  }, "Impossible de compter ce que le renommage touche.");
}

export async function updateClientCorrespondence(correspondenceId, payload) {
  return requestJson(`/api/data-manager/client-correspondences/${encodeURIComponent(correspondenceId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible d'enregistrer le client.");
}

export async function prepareTruckDispatchUpdate(truckId, { dispatchDate, dispatchPeriod, expectedUpdatedAt }) {
  if (!dispatchDate || !expectedUpdatedAt) {
    throw new Error("La journée ou la révision du camion enregistré est introuvable.");
  }
  const searchParams = new URLSearchParams({
    dispatch_date: dispatchDate,
    dispatch_period: dispatchPeriod === "evening" ? "evening" : "day",
  });
  return requestJson(
    `/api/planning/source-changes/trucks/${encodeURIComponent(truckId)}/prepare?${searchParams}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ expected_updated_at: expectedUpdatedAt }),
    },
    "La préparation du camion dans le Dispatch a échoué.",
  );
}

// Without expectedUpdatedAt, the order was deleted and only leaves its dispatch.
export async function prepareOrderDispatchUpdate(orderId, { dispatchDate, dispatchPeriod, expectedUpdatedAt }) {
  if (!dispatchDate) throw new Error("La journée de la commande est introuvable.");
  const searchParams = new URLSearchParams({ dispatch_date: dispatchDate, dispatch_period: dispatchPeriod === "evening" ? "evening" : "day" });
  return requestJson(`/api/planning/source-changes/orders/${encodeURIComponent(orderId)}/prepare?${searchParams}`, {
    method: "POST",
    // The Dispatch rereads Mise à jour itself: skip the post-commit previews.
    headers: { "Content-Type": "application/json", "X-TransIA-Source-Changes-Refresh": "deferred" },
    body: JSON.stringify(expectedUpdatedAt ? { expected_updated_at: expectedUpdatedAt } : {}),
  }, "La mise à jour de la commande dans le Dispatch a échoué.");
}

export async function prepareLoadingConstraintDispatchUpdate({ correspondenceId, expectedUpdatedAt, dispatchDate, dispatchPeriod = "day" } = {}) {
  if (!dispatchDate || !correspondenceId || !expectedUpdatedAt) throw new Error("La journée ou la révision du client sauvegardé est introuvable.");
  const searchParams = new URLSearchParams({ dispatch_date: dispatchDate, dispatch_period: dispatchPeriod === "evening" ? "evening" : "day" });
  return requestJson(`/api/planning/source-changes/clients/${encodeURIComponent(correspondenceId)}/prepare?${searchParams}`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ expected_updated_at: expectedUpdatedAt }),
  }, "Impossible de préparer la mise à jour de la meunerie dans le Dispatch.");
}

export async function updateEmployee(employeeId, payload) {
  return requestJson(`/api/data-manager/employees/${encodeURIComponent(employeeId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible d'enregistrer le chauffeur.");
}

export async function updateProduct(productId, payload) {
  return requestJson(`/api/data-manager/products/${encodeURIComponent(productId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible d'enregistrer le code produit.");
}

export async function deleteTruck(truckId, payload) {
  return requestJson(`/api/data-manager/trucks/${encodeURIComponent(truckId)}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de supprimer le camion.");
}

// « Retirer de la flotte » / « Remettre dans la flotte » : sans confirmation, le serveur rend la fenêtre à confirmer (409).
async function fleetRetirementRequest(truckId, action, payload, { previewCode, fallbackMessage }) {
  const response = await fetchWithSupabaseAuth(`/api/data-manager/trucks/${encodeURIComponent(truckId)}/${action}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const body = await response.json().catch(() => null);
  if (response.status === 409 && body?.detail?.code === previewCode) return { preview: body.detail };
  if (!response.ok) {
    const error = new Error(resolveErrorMessage(body, fallbackMessage));
    error.status = response.status;
    throw error;
  }
  return { result: body };
}

export function retireTruck(truckId, payload) {
  return fleetRetirementRequest(truckId, "retire", payload, {
    previewCode: "fleet_retirement_confirmation_required", fallbackMessage: "Impossible de retirer de la flotte.",
  });
}

export function reinstateTruck(truckId, payload) {
  return fleetRetirementRequest(truckId, "reinstate", payload, {
    previewCode: "fleet_reinstatement_confirmation_required", fallbackMessage: "Impossible de remettre dans la flotte.",
  });
}

export async function deleteClientCorrespondence(correspondenceId, payload) {
  return requestJson(`/api/data-manager/client-correspondences/${encodeURIComponent(correspondenceId)}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de supprimer le client.");
}

export async function deleteEmployee(employeeId, payload) {
  return requestJson(`/api/data-manager/employees/${encodeURIComponent(employeeId)}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de supprimer le chauffeur.");
}

export async function deleteProduct(productId, payload) {
  return requestJson(`/api/data-manager/products/${encodeURIComponent(productId)}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de supprimer le code produit.");
}

export async function createCompartment(truckId, payload) {
  return requestJson(`/api/data-manager/trucks/${encodeURIComponent(truckId)}/compartments`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de créer le compartiment.");
}

export async function updateCompartment(truckId, compartmentId, payload) {
  return requestJson(`/api/data-manager/trucks/${encodeURIComponent(truckId)}/compartments/${encodeURIComponent(compartmentId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible d'enregistrer le compartiment.");
}

export async function deleteCompartment(truckId, compartmentId, payload) {
  return requestJson(`/api/data-manager/trucks/${encodeURIComponent(truckId)}/compartments/${encodeURIComponent(compartmentId)}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de supprimer le compartiment.");
}

export async function createOrder(payload, options = {}) {
  return requestJson("/api/data-manager/orders", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de créer la commande.", options);
}

export async function createOrders(payload, options = {}) {
  return requestJson("/api/data-manager/orders/bulk", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de créer les commandes.", options);
}

export async function updateOrder(orderId, payload, options = {}) {
  return requestJson(`/api/data-manager/orders/${encodeURIComponent(orderId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible d'enregistrer la commande.", options);
}

// Numéro de référence du chargement : même valeur que dans Consolidation, sans modifier la commande.
export async function updateOrderLoadingReference(orderId, reference) {
  return requestJson(`/api/data-manager/orders/${encodeURIComponent(orderId)}/loading-reference`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reference: reference || null }),
  }, "Impossible d'enregistrer le numéro de référence.");
}

export async function updateOrderExecution(orderId, payload) {
  return requestJson(`/api/data-manager/orders/${encodeURIComponent(orderId)}/execution`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible d'enregistrer l'exécution réelle de la commande.");
}

export async function assignOrdersEndOfDayLoad(payload) {
  return requestJson("/api/data-manager/orders/end-of-day-loads/assign", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ target_suspension_decision: "ask", ...payload }),
  }, "Impossible d'appliquer Chargé en PM.");
}

export async function removeOrdersEndOfDayLoad(payload) {
  return requestJson("/api/data-manager/orders/end-of-day-loads/remove", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de retirer Chargé en PM.");
}

export async function deleteOrder(orderId, payload) {
  return requestJson(`/api/data-manager/orders/${encodeURIComponent(orderId)}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de supprimer la commande.");
}

export async function clearOrderDeliveredStatus(orderId, payload) {
  return requestJson(`/api/data-manager/orders/${encodeURIComponent(orderId)}/clear-delivered`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de retirer le statut livré.");
}

export async function validateOrderAddress(payload) {
  return requestJson("/api/data-manager/orders/validate-address", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de valider l'adresse.");
}

export async function cancelOrder(orderId, payload) {
  return requestJson(`/api/data-manager/orders/${encodeURIComponent(orderId)}/cancel`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible d'annuler la commande.");
}

export async function reopenOrder(orderId, payload) {
  return requestJson(`/api/data-manager/orders/${encodeURIComponent(orderId)}/reopen`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de rouvrir la commande.");
}

export async function downloadDataManagerExport(url, fileName) {
  return downloadDataManagerFile(url, fileName, "Impossible de télécharger l'export.");
}

export async function previewDispatchSavesImport(items) {
  return requestJson("/api/data-manager/import/dispatch-saves/preview", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  }, "Impossible d'analyser le fichier de sauvegardes dispatch.");
}

export async function importDispatchSavesSelection({ items, selectedImportKeys }) {
  return requestJson("/api/data-manager/import/dispatch-saves", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      items,
      selected_import_keys: selectedImportKeys,
    }),
  }, "Impossible d'importer les sauvegardes dispatch sélectionnées.");
}

export async function fetchRailwaySyncAvailability() {
  return requestJson("/api/data-manager/railway-business-copy/availability", {
    method: "GET",
    headers: { Accept: "application/json" },
  }, "Impossible de vérifier la disponibilité de la synchronisation Railway.");
}

export async function copyRailwayBusinessToLocal({ onProgress, start_date, end_date }) {
  const progressId = crypto.randomUUID();
  let stopped = false;
  let timer;
  let pollController;
  const reportedStatuses = new Map();
  const report = (key, status) => {
    if (reportedStatuses.get(key) === status) return;
    reportedStatuses.set(key, status);
    onProgress?.(key, status);
  };
  const poll = async () => {
    pollController = new AbortController();
    const timeout = setTimeout(() => pollController.abort(), 3000);
    try {
      const snapshot = await requestJson(`/api/data-manager/railway-sync/progress/${progressId}`, {
        signal: pollController.signal,
        cache: "no-store",
      }, "Suivi d'import Railway indisponible.");
      if (!stopped) {
        for (const step of snapshot?.steps || []) report(step.key, step.status);
      }
    } catch {
      // A missing first snapshot or a failed poll must never retry or stop the import.
    } finally {
      clearTimeout(timeout);
      if (!stopped) timer = setTimeout(poll, 750);
    }
  };
  if (progressId) timer = setTimeout(poll, 750);
  try {
    let result;
    try {
      result = await requestJson("/api/data-manager/railway-business-copy", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        progress_id: progressId,
        start_date,
        end_date,
      }),
    }, "Impossible de copier les données de production en local.");
    } catch (error) {
      // A committed copy can outlive the POST connection. Never replay the POST.
      try {
        result = await requestJson(`/api/data-manager/railway-business-copy/result/${progressId}`, { cache: "no-store" }, "Résultat indisponible.");
      } catch {
        throw error;
      }
    }
    if (result?.verified !== true) throw new Error("La copie n’a pas été confirmée comme vérifiée.");
    if (progressId) {
      for (const step of RAILWAY_BUSINESS_COPY_PROGRESS_STEPS) {
        if (step.key !== "refresh") report(step.key, "completed");
      }
    }
    return result;
  } finally {
    stopped = true;
    clearTimeout(timer);
    pollController?.abort();
  }
}

export async function fetchColumnPreferences(tableKey) {
  return requestJson(
    `/api/data-manager/column-preferences/${encodeURIComponent(tableKey)}`,
    undefined,
    "Impossible de charger les préférences de colonnes.",
  );
}

export async function saveColumnPreferences(
  tableKey,
  { columnLabels = {}, columnOrder = [], columnWidths = {}, frozenColumnKeys = null } = {},
  { keepalive = false } = {},
) {
  const payload = {
    column_labels: columnLabels,
    column_order: columnOrder,
    column_widths: columnWidths,
  };
  if (Array.isArray(frozenColumnKeys)) {
    payload.frozen_column_keys = frozenColumnKeys;
  }
  const result = await requestJson(`/api/data-manager/column-preferences/${encodeURIComponent(tableKey)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    ...(keepalive === true ? { keepalive: true } : {}),
  }, "Impossible de sauvegarder les préférences de colonnes.");
  invalidatePageDataCacheEntries(["/api/data-manager/state"]);
  return result;
}
